import type { ExpandedEvent } from '../lib/rrule';
import SideDrawer, { SideDrawerHeader } from './SideDrawer';

interface DayEventsModalProps {
  date: Date;
  events: ExpandedEvent[];
  calendarColorMap: Record<string, string>;
  currentCalendarColor: string;
  holidays?: string[];
  onClose: () => void;
  onEventClick: (ev: ExpandedEvent) => void;
  onCreateEvent: () => void;
}

const DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export default function DayEventsModal({
  date,
  events,
  calendarColorMap,
  currentCalendarColor,
  holidays = [],
  onClose,
  onEventClick,
  onCreateEvent,
}: DayEventsModalProps) {
  const allDayEvents = events.filter(e => e.all_day);
  const timedEvents = events.filter(e => !e.all_day);

  // Sort timed events by start time
  const sortedTimedEvents = [...timedEvents].sort(
    (a, b) =>
      new Date(a.start_at).getTime() -
      new Date(b.start_at).getTime()
  );

  const dayName = DAYS[date.getDay()];
  const monthName = MONTHS[date.getMonth()];
  const dayNumber = date.getDate();

  return (
    <SideDrawer onClose={onClose}>
      <SideDrawerHeader
        title={`${dayName}, ${monthName} ${dayNumber}`}
        accentColor={currentCalendarColor}
        onClose={onClose}
      />

      {/* Body */}
      <div className="flex-1 overflow-y-auto flex flex-col">
        {/* Sticky Add Event Button at Top */}
        <div className="sticky top-0 z-10 bg-paper-50 p-5 pb-3 border-b border-paper-200 shadow-sm">
          <button
            onClick={onCreateEvent}
            className="w-full py-3 px-4 rounded-sm font-mono text-sm text-white hover:opacity-90 transition-opacity shadow-sm"
            style={{
              backgroundColor: currentCalendarColor,
            }}
          >
            + Add Event
          </button>
        </div>

        <div className="p-5 pt-3 space-y-5">
          {/* Holidays Section — pinned at top, unformatted */}
          {holidays.length > 0 && (
            <div className="space-y-1">
              {holidays.map((name, idx) => (
                <p
                  key={`holiday-${idx}`}
                  className="font-mono text-xs text-ink-400 italic"
                >
                  🗓 {name}
                </p>
              ))}
            </div>
          )}

          {/* All-Day Events Section */}
          {allDayEvents.length > 0 && (
            <div>
              <h3 className="font-mono text-xs text-ink-400 uppercase tracking-widest mb-3">
                All-Day Events ({allDayEvents.length})
              </h3>
              <div className="space-y-2">
                {allDayEvents.map(ev => {
                  const color =
                    (ev as { subscription_color?: string })
                      .subscription_color ??
                    calendarColorMap[ev.calendar_id] ??
                    currentCalendarColor;
                  return (
                    <button
                      key={ev.id}
                      onClick={() => onEventClick(ev)}
                      className="w-full text-left px-3 py-2 rounded-sm text-white font-mono text-sm hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: color }}
                    >
                      {ev.title}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Timed Events Section */}
          {sortedTimedEvents.length > 0 && (
            <div>
              <h3 className="font-mono text-xs text-ink-400 uppercase tracking-widest mb-3">
                Events ({sortedTimedEvents.length})
              </h3>
              <div className="space-y-2">
                {sortedTimedEvents.map(ev => {
                  const color =
                    (ev as { subscription_color?: string })
                      .subscription_color ??
                    calendarColorMap[ev.calendar_id] ??
                    currentCalendarColor;
                  return (
                    <button
                      key={ev.id}
                      onClick={() => onEventClick(ev)}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-sm bg-paper-100 hover:bg-paper-200 transition-colors text-left"
                    >
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: color }}
                      />
                      <span className="font-mono text-xs text-ink-400 shrink-0 w-16">
                        {formatTime(ev.start_at)}
                      </span>
                      <span className="font-mono text-sm text-ink-800 truncate">
                        {ev.title}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty State */}
          {events.length === 0 && holidays.length === 0 && (
            <div className="text-center py-8">
              <p className="font-mono text-sm text-ink-400">
                No events on this day
              </p>
            </div>
          )}
        </div>
      </div>
    </SideDrawer>
  );
}
